import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '@utils/api';
import { useI18n } from '@/i18n';
import logoWordmark from '../assets/logo-glk-wordmark.png';

// Public "forgot password" request. Always shows the same confirmation whether
// or not the email matches an account (the API is anti-enumeration).
export default function ForgotPassword() {
  const { t } = useI18n();
  const [email,   setEmail]   = useState('');
  const [sent,    setSent]    = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await api.post('/access/forgot', { email }); }
    catch { /* swallow — never reveal whether the email exists */ }
    finally { setLoading(false); setSent(true); }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          background:
            'radial-gradient(90% 60% at 50% -10%, rgba(45,125,70,0.18), transparent 60%), ' +
            'radial-gradient(70% 50% at 90% 110%, rgba(26,94,158,0.14), transparent 60%), #F7F8FA',
        }}
      />
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-sm"
      >
        <div className="mb-8 text-center">
          <img src={logoWordmark} alt="GroundLink III Handling" className="h-16 w-auto mx-auto" />
          <p className="mt-3 text-xs font-mono text-gray-400 uppercase tracking-[0.2em]">{t('common.portalName')}</p>
        </div>

        <div className="card p-8 backdrop-blur-sm bg-white/90">
          {sent ? (
            <div className="text-center py-2">
              <div className="mx-auto mb-4 grid place-items-center w-12 h-12 rounded-full bg-green-100 text-green-700">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m22 2-7 20-4-9-9-4Z" /><path d="M22 2 11 13" /></svg>
              </div>
              <h1 className="text-lg font-semibold text-glk-dark mb-1">{t('forgot.sentTitle')}</h1>
              <p className="text-sm text-gray-500 mb-6">{t('forgot.sentLead', { email })}</p>
              <Link to="/login" className="btn-primary w-full">{t('forgot.back')}</Link>
            </div>
          ) : (
            <>
              <h1 className="text-lg font-semibold text-glk-dark mb-1">{t('forgot.title')}</h1>
              <p className="text-sm text-gray-500 mb-6">{t('forgot.lead')}</p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="email" className="label">{t('forgot.email')}</label>
                  <input id="email" type="email" autoComplete="email" required className="input"
                         value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
                <motion.button type="submit" disabled={loading} whileTap={{ scale: 0.98 }} className="btn-primary w-full mt-2">
                  {loading ? t('forgot.sending') : t('forgot.send')}
                </motion.button>
              </form>

              <p className="mt-5 text-center text-sm">
                <Link to="/login" className="text-glk-green hover:underline">{t('forgot.back')}</Link>
              </p>
            </>
          )}
        </div>

        <p className="mt-6 text-center text-[11px] text-gray-400">
          {t('common.footer')}
        </p>
      </motion.div>
    </div>
  );
}
